import { isToday, startOfDay } from 'date-fns';
import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import useSWR from 'swr';
import { scheduledCollectServices, storeService } from 'services';
import { useAuth, useScreenInfo } from 'hooks';
import {
  CollectConsignmentWithDate,
  FormOption,
  ScheduleCollectConsignmentData,
  ScheduleCollectGroup,
} from 'types';
import { NotFoundIcon } from 'static/icons';
import { Carrier, History } from 'pages/batch-collection/components';
import { Button, Heading, Loading, Select, Text } from 'components';
import { removeLocalStorageItem } from 'helpers';
import {
  FontSize,
  FontWeight,
  LocalStorageKeys,
  TEST_IDS,
  deliveryModes,
} from 'commons';
import * as S from './styles';

const UNKNOWN_CARRIER = {
  label: 'Outra',
  value: 'UNKNOWN_CARRIER',
};

export function SelectConsignmentsOutlet() {
  const { isDesktop, isSmallDesktop, isTablet, isMobile } = useScreenInfo();
  const { selectedStore } = useAuth();
  const store = selectedStore.name || '';
  const currentDate = new Date();

  const [selectedConsignmentsCodes, setSelectedConsignmentsCodes] = useState<
    string[]
  >([]);

  const [availableDeliveryModes, setAvailableDeliveryModes] = useState<
    FormOption[]
  >([]);
  const [availableConsignmentCodes, setAvailableConsignmentCodes] = useState<
    FormOption[]
  >([]);

  useEffect(() => {
    setSelectedConsignmentsCodes([]);
  }, [store]);

  removeLocalStorageItem(LocalStorageKeys.ConsignmentsToCollect);

  const {
    data: scheduledCollectData,
    isLoading,
    mutate,
  } = useSWR(
    `collection-${store}`,
    () =>
      scheduledCollectServices.get({
        store,
        codes: values.consignmentCodeList || undefined,
        deliveryMode: values.deliveryMode || undefined,
      }),
    {
      revalidateOnFocus: false,
    }
  );

  const { data: historyData } = useSWR(
    `bath-collection-history-${store}`,
    () =>
      storeService.getBatchCollectsHistory({
        store,
      }),
    {
      revalidateOnFocus: false,
    }
  );

  const { handleChange, handleSubmit, values } = useFormik({
    initialValues: {
      consignmentCodeList: '',
      deliveryMode: '',
    },

    onSubmit: () => {
      mutate();
    },
  });

  function getConsignmentCodeFormOption(
    scheduledCollectGroup: ScheduleCollectGroup[]
  ): FormOption[] {
    const availableConsigments: ScheduleCollectConsignmentData[] = [];

    scheduledCollectGroup.forEach(({ consignments }) =>
      consignments.forEach((consignment) =>
        availableConsigments.push(consignment)
      )
    );

    const currConsignmentsCodes = availableConsigments.map(
      ({ consignmentCode }) => {
        return { label: consignmentCode, value: consignmentCode };
      }
    );

    return currConsignmentsCodes;
  }

  function getDeliveryModeFormOption(
    scheduledCollectGroup: ScheduleCollectGroup[]
  ): FormOption[] {
    const uniqueDeliveryModes = new Set<FormOption>();

    scheduledCollectGroup.forEach(({ deliveryModeId }) => {
      const currDeliveryMode = deliveryModes.find(
        ({ value }) => value === deliveryModeId
      );

      if (!currDeliveryMode) {
        UNKNOWN_CARRIER.value = deliveryModeId;
        uniqueDeliveryModes.add(UNKNOWN_CARRIER);
      } else {
        uniqueDeliveryModes.add(currDeliveryMode);
      }
    });

    return [...uniqueDeliveryModes];
  }

  const populateSelectOptions = useCallback(() => {
    if (!scheduledCollectData || !Boolean(scheduledCollectData.length)) return;

    const currDeliveryModes = getDeliveryModeFormOption(scheduledCollectData);
    const currConsignmentsCodes =
      getConsignmentCodeFormOption(scheduledCollectData);

    setAvailableDeliveryModes(currDeliveryModes);
    setAvailableConsignmentCodes(currConsignmentsCodes);
  }, [scheduledCollectData]);

  useEffect(() => {
    populateSelectOptions();
  }, [populateSelectOptions]);

  function handleSelectAllConsignments(
    consignmentCodes: string[],
    remove?: boolean
  ) {
    if (remove) {
      setSelectedConsignmentsCodes((current) =>
        current.filter((code) =>
          consignmentCodes.every((consignmentCode) => consignmentCode !== code)
        )
      );
    } else {
      setSelectedConsignmentsCodes((current) => [
        ...current,
        ...consignmentCodes,
      ]);
    }
  }

  function handleSelectConsignment(consignmentCode: string) {
    const isConsignmentCodeOnTheList = selectedConsignmentsCodes.filter(
      (code) => code === consignmentCode
    );

    if (Boolean(!isConsignmentCodeOnTheList.length)) {
      setSelectedConsignmentsCodes((prevState) => [
        ...prevState,
        consignmentCode,
      ]);
    } else {
      setSelectedConsignmentsCodes((prevState) => {
        const currentCodes = prevState.filter(
          (code) => code !== consignmentCode
        );
        return currentCodes;
      });
    }
  }

  function associateExpectedDateWithConsignment(
    scheduledCollectGroup: ScheduleCollectGroup[]
  ): ScheduleCollectGroup[] {
    return scheduledCollectGroup.map((collect) => {
      const { consignments, expectedDate } = collect;

      const consignmentsWithExpiringDate: CollectConsignmentWithDate[] =
        consignments.map((consignment) => ({
          ...consignment,
          expectedDate,
        }));

      return {
        ...collect,
        consignments: consignmentsWithExpiringDate,
      };
    });
  }

  function mergeCollects(
    scheduledCollects: ScheduleCollectGroup[]
  ): ScheduleCollectGroup[] {
    const mergedCollects: ScheduleCollectGroup[] = [];

    scheduledCollects.forEach((collect) => {
      const { deliveryModeId, driverName, driverCpf } = collect;

      const existingCollect = mergedCollects.findIndex(
        (mergedCollect) =>
          mergedCollect.deliveryModeId === deliveryModeId &&
          mergedCollect.driverName &&
          mergedCollect.driverName === driverName &&
          mergedCollect.driverCpf &&
          mergedCollect.driverCpf === driverCpf
      );

      if (existingCollect !== -1) {
        mergedCollects[existingCollect].consignments.push(
          ...collect.consignments
        );
      } else {
        const newCollect: ScheduleCollectGroup = { ...collect };
        mergedCollects.push(newCollect);
      }
    });

    return mergedCollects;
  }

  function renderCarrierColumn(
    scheduledCollects: ScheduleCollectGroup[],
    options?: { defaultOpen?: boolean }
  ) {
    const updatedCollectConsignments =
      associateExpectedDateWithConsignment(scheduledCollects);
    const mergedCScheduledCollects = mergeCollects(updatedCollectConsignments);

    return mergedCScheduledCollects.map((entry) => {
      const { consignments, deliveryModeId, driverName } = entry;
      const { defaultOpen } = options || {};
      const carrier: FormOption = deliveryModes.find(
        (deliveryMode) => deliveryMode.value === deliveryModeId
      ) || { label: 'Transp. desconhecida', value: '' };

      return (
        <Carrier
          key={consignments[0].consignmentCode}
          carrier={carrier}
          driverName={driverName}
          defaultOpen={defaultOpen}
          consignmentList={consignments}
          handleSelectAllConsignments={handleSelectAllConsignments}
          handleSelectConsignment={handleSelectConsignment}
        />
      );
    });
  }

  const renderTodayCollection = (() => {
    if (!scheduledCollectData) return null;

    const todayCollection = scheduledCollectData.filter(
      (data: ScheduleCollectGroup) =>
        data.expectedDate && isToday(data.expectedDate)
    );

    return renderCarrierColumn(todayCollection, { defaultOpen: true });
  })();

  const renderFutureCollection = (() => {
    if (!scheduledCollectData) return null;

    const futureCollection = scheduledCollectData.filter(
      (data: ScheduleCollectGroup) =>
        data.expectedDate &&
        startOfDay(data.expectedDate) > startOfDay(currentDate)
    );

    return renderCarrierColumn(futureCollection);
  })();

  const renderWaitingCollection = (() => {
    if (!scheduledCollectData) return null;

    const waitingCollection = scheduledCollectData.filter(
      (data: ScheduleCollectGroup) =>
        !data.expectedDate ||
        startOfDay(data.expectedDate) < startOfDay(currentDate)
    );

    return renderCarrierColumn(waitingCollection);
  })();

  const carrierColumns = (() => (
    <S.CarrierColumnsByDate>
      {Boolean(renderTodayCollection?.length) && (
        <S.BatchWrapper>
          <Heading size={FontSize.font16}>Coletas de hoje</Heading>

          <S.CarriersColumns data-testid={TEST_IDS.CARRIERS_WRAPPER_DIV}>
            {renderTodayCollection}
          </S.CarriersColumns>
        </S.BatchWrapper>
      )}

      <S.BatchWrapper $isRow>
        {Boolean(renderFutureCollection?.length) && (
          <S.BatchWrapper>
            <Heading size={FontSize.font16}>Coletas futuras</Heading>

            <S.BatchWrapper data-testid={TEST_IDS.CARRIERS_WRAPPER_DIV}>
              {renderFutureCollection}
            </S.BatchWrapper>
          </S.BatchWrapper>
        )}
        {Boolean(renderWaitingCollection?.length) && (
          <S.BatchWrapper>
            <Heading size={FontSize.font16}>Aguardando</Heading>

            <S.BatchWrapper data-testid={TEST_IDS.CARRIERS_WRAPPER_DIV}>
              {renderWaitingCollection}
            </S.BatchWrapper>
          </S.BatchWrapper>
        )}
      </S.BatchWrapper>
    </S.CarrierColumnsByDate>
  ))();

  const maxTags = (() => {
    if (isDesktop) return 3;

    if (isSmallDesktop) return 2;

    if (isTablet) return 2;

    return 1;
  })();

  return (
    <S.Main>
      <Text>
        Acompanhe os pedidos coletados ou que estão aguardando um transportador.
      </Text>

      {historyData && Boolean(historyData.length) && (
        <S.HistoryWrapper data-testid={TEST_IDS.BATCH_COLLECTION_HISTORY}>
          {historyData.map((history) => (
            <History key={history.consignments[0].code} historyData={history} />
          ))}
        </S.HistoryWrapper>
      )}

      <S.IntroTextWrapper>
        <Heading weight={FontWeight.bold}>Registro de Coleta</Heading>
        <Text>Selecione os pedidos que o transportador irá levar.</Text>
      </S.IntroTextWrapper>

      <S.FilterForm onSubmit={handleSubmit}>
        <S.SelectWrapper>
          <Text size={FontSize.font12}>Pesquise pelos pedidos</Text>

          <Select.Wrapper
            data-testid={TEST_IDS.CONSIGNMENT_CODE_SELECT}
            key={store}
            name="consignmentCodeList"
            onChange={handleChange}
            options={availableConsignmentCodes}
            multiple
            searchable
          >
            <Select.Icon>
              <MdSearch />
            </Select.Icon>

            <Select.Input
              type="tel"
              allowPasteList
              placeholder="Digite o número do pedido"
            />

            <Select.Tags maxTags={maxTags} />

            <Select.ClearButton />

            <Select.ScanCodesButton hideButton={!isTablet && !isMobile} />
          </Select.Wrapper>
        </S.SelectWrapper>

        <S.SelectAndButton>
          <S.SmallSelectWrapper>
            <Text size={FontSize.font12}>Selecione uma transportadora</Text>

            <Select.Wrapper
              data-testid={TEST_IDS.DELIVERY_MODE_SELECT}
              name="deliveryMode"
              onChange={handleChange}
              options={availableDeliveryModes}
            >
              <Select.Input placeholder="Transportadora" />

              <Select.ClearButton />

              <Select.StatusArrow />
            </Select.Wrapper>
          </S.SmallSelectWrapper>

          <Button data-testid={TEST_IDS.APPLY_FILTER_BUTTON} type="submit">
            Filtrar
          </Button>
        </S.SelectAndButton>
      </S.FilterForm>

      <S.Wrapper>
        {!isLoading &&
          !Boolean(renderTodayCollection?.length) &&
          !Boolean(renderFutureCollection?.length) &&
          !Boolean(renderWaitingCollection?.length) && (
            <S.NoBatchToCollect>
              <NotFoundIcon />
              <Text>Nenhum pedido para coletar</Text>
            </S.NoBatchToCollect>
          )}

        {isLoading ? <Loading /> : carrierColumns}
      </S.Wrapper>
    </S.Main>
  );
}
